import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getEducationArticles } from '../../../api/homepageManagementEducationInfo.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IEducationArticleItem,
  IGetEducationArticles,
} from '../../../models/homepageManagementEducationInfo.model';
import { RootState } from '../../../store/rootReducer';

const HomepageEducation = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // BR0014 = 정보게시판 구분
  const BR0014 = commonCodes.find(
    (item) => item.commonCode === 'BR0014',
  )?.detailCode;
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetEducationArticles>();

  const [bulletinDiv, setBulletinDiv] = useState<string>();
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});

  useEffect(() => {
    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(Number(query.page));
    }
    if (query.bulletinDiv) {
      _param.bulletinDiv = String(query.bulletinDiv);
      setBulletinDiv(_param.bulletinDiv);
    }
    if (query.title) {
      _param.title = String(query.title);
      setTitle(_param.title);
    }

    execute(getEducationArticles(_param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  }, [location]);

  const onSearch = () => {
    const param: any = { page: 1 };

    if (bulletinDiv) param.bulletinDiv = bulletinDiv;
    if (title) param.title = title;

    history.push(`/homepage/education?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<IEducationArticleItem> = [
    {
      title: t('homepage.archive.column1'),
      dataIndex: 'bulletinCode',
      key: 'bulletinCode',
      align: 'center',
      width: '6%',
      sorter: (a, b) => a.bulletinCode - b.bulletinCode,
    },
    {
      title: t('homepage.education.column1'),
      dataIndex: 'bulletinDiv',
      key: 'bulletinDiv',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.bulletinDiv?.localeCompare(b.bulletinDiv),
      render: (value) => getCommonCodeLabel('BR0014', value, i18n.language),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title?.localeCompare(b.title),
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('homepage.archive.column5'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('common.column.writer'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regMemKorName?.localeCompare(b.regMemKorName),
    },
    {
      title: t('homepage.archive.column7'),
      dataIndex: 'readCount',
      key: 'readCount',
      align: 'center',
      width: '7%',
      sorter: (a, b) => a.readCount - b.readCount,
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t('common.menu.homepage')}
      currentMenu={t('homepage.education.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('homepage.education.column1')}</label>
            <div>
              <Select
                placeholder={t('homepage.education.column1')}
                value={bulletinDiv}
                onChange={(value) => setBulletinDiv(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {BR0014?.map((item: any) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {i18n.language === 'ko' ? item.korName : item.engName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('common.column.title')}</label>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('homepage.archive.placeholder1')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}</div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/education/create')}
              text={t('homepage.archive.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/homepage/education/${record.bulletinCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="noticeCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/homepage/education"
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/education/create')}
              text={t('homepage.archive.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default HomepageEducation;
